<template>
  <div class="relative">

    <div class="vx-navbar-wrapper" :class="classObj">
      <vs-navbar class="vx-navbar navbar-custom navbar-skelton" :color="navbarColorLocal" :class="textColor">
        <!-- SM - OPEN SIDEBAR BUTTON-->
        <feather-icon class="sm:inline-flex xl:hidden cursor-pointer p-2" icon="MenuIcon" @click.stop="showSidebar" />

        <template v-if="!isMobile">
          <div v-if="accessGranted" class="navbar-group navbar-group-stats">
            <div class="text-gray-500 flex items-center" title="CPU usage">
              <feather-icon icon="CpuIcon" class="w-5 h-5 navbar-icon mr-2" />
              <span>{{ (serverData.cpu || 0).toFixed(0) }}%</span>
            </div>
            <div class="text-gray-500 flex items-center" title="Memory usage">
              <feather-icon icon="BarChartIcon" class="w-5 h-5 navbar-icon mr-2" />
              <span>{{ memoryPercent }}%</span>
            </div>
            <server-area :activeConnectionsList="activeConnectionsList" />
          </div>

          <span v-if="accessGranted" class="navbar-divider"></span>

          <div class="navbar-group">
            <router-link to="/scale/pesees" class="flex items-center text-gray-500 hover:text-primary relative" :title="`${weighingHourCount} weighing(s) this hour`">
              <svg class="w-5 h-5 weighing-icon navbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 3v18" />
                <path d="M5 21h14" />
                <path d="M5 7h14" />
                <path d="M2 13l3-6 3 6a3 3 0 0 1-6 0z" />
                <path d="M16 13l3-6 3 6a3 3 0 0 1-6 0z" />
              </svg>
              <span v-if="weighingHourCount > 0" class="weighing-bubble">{{ weighingHourCount }}</span>
            </router-link>
          </div>

          <span v-if="solarStations.length > 0" class="navbar-divider"></span>
          <div v-if="solarStations.length > 0" class="navbar-group solar-trigger-group">
            <a
              href="#"
              class="flex items-center text-gray-500 hover:text-primary solar-navbar-trigger"
              @click.prevent="openSolarPopup"
              :title="solarmanOffline ? 'Solarman unreachable — offline data, values not refreshed' : 'Solar production'"
            >
              <feather-icon icon="ZapIcon" class="w-5 h-5 navbar-icon mr-2" />
              <span v-if="solarmanOffline" class="solarman-offline-pill">
                <feather-icon icon="AlertTriangleIcon" svgClasses="h-3 w-3" class="mr-1" />
                Solarman offline
              </span>
              <span v-else class="solar-live-value">{{ solarLiveText }}</span>
            </a>

            <vs-dropdown
              v-if="solarWorstScore !== null"
              vs-custom-content
              vs-trigger-click
              class="cursor-pointer ml-2"
            >
              <span
                class="solar-score-pill"
                :class="scoreClass"
                :title="`Solar health score — click for details`"
              >
                <feather-icon icon="ActivityIcon" svgClasses="h-3 w-3" class="solar-score-ico" />
                {{ solarWorstScore }}
              </span>
              <vs-dropdown-menu class="solar-popover">
                <div class="solar-popover-inner">
                  <h6 class="solar-popover-title">Solar health</h6>
                  <div v-if="solarScoresList.length === 0" class="text-grey">No score yet.</div>
                  <div
                    v-for="s in solarScoresList"
                    :key="'sc-pop-' + s.stationId"
                    class="solar-popover-row"
                  >
                    <span class="solar-popover-station">{{ s.name || ('Station ' + s.stationId) }}</span>
                    <span class="solar-popover-score" :class="scoreClassFor(s.score)">{{ s.score }}</span>
                    <small class="solar-popover-level">{{ s.level }}</small>
                    <div class="solar-popover-breakdown" v-if="s.breakdown">
                      Availability (24h): {{ Number(s.breakdown.availability_30 || 0).toFixed(1) }}/30
                    </div>
                  </div>
                  <div class="solar-popover-foot">
                    <small>Score is recomputed every minute by the collector. Range 0–100, ≥85 healthy / ≥60 degraded / &lt;60 critical.</small>
                  </div>
                </div>
              </vs-dropdown-menu>
            </vs-dropdown>

            <vs-dropdown
              vs-custom-content
              vs-trigger-click
              class="cursor-pointer ml-2"
            >
              <span
                class="solar-alerts-badge"
                :class="bellClass"
                :title="solarTotalAlertsCount === 0 ? 'No active alerts' : `${solarCriticalAlertsCount} critical, ${solarWarningAlertsCount} warning`"
              >
                <feather-icon icon="BellIcon" svgClasses="h-3 w-3" class="solar-alerts-bell" />
                {{ solarTotalAlertsCount }}
              </span>
              <vs-dropdown-menu class="solar-popover">
                <div class="solar-popover-inner">
                  <h6 class="solar-popover-title">
                    Active alerts
                    <span class="text-grey" style="font-weight: 400; font-size: 11px;">
                      ({{ solarCriticalAlertsCount }} critical, {{ solarWarningAlertsCount }} warning)
                    </span>
                  </h6>
                  <div v-if="solarAlertsList.length === 0" class="text-grey" style="padding: 8px 0;">
                    No active alerts. All inverters look healthy 🎉
                  </div>
                  <div
                    v-for="a in solarAlertsList"
                    :key="'al-pop-' + a.id"
                    class="solar-popover-alert"
                  >
                    <span class="alert-sev-pill" :class="'sev-' + a.severity">{{ a.severity }}</span>
                    <span class="alert-rule">{{ ruleLabel(a.ruleCode) }}</span>
                    <div class="alert-meta">
                      <span>{{ a.stationName || ('Station ' + a.stationId) }}</span>
                      <span v-if="a.inverterSn" class="text-grey ml-1">/ {{ a.inverterSn }}</span>
                      <span class="text-grey ml-1">— {{ formatRelativeShort(a.openedAt) }}</span>
                    </div>
                  </div>
                  <div class="solar-popover-foot" v-if="accessGranted">
                    <small>
                      <router-link to="/solar-admin">Open Solar Admin →</router-link>
                    </small>
                  </div>
                </div>
              </vs-dropdown-menu>
            </vs-dropdown>
          </div>
          <bookmarks :navbarColor="navbarColor" v-if="windowWidth >= 992" />
        </template>

        <vs-spacer />

        <template v-if="!isMobile">
          <i18n />
          <call-center />
          <restricted-area v-if="accessGranted" />
          <help-button />
        </template>

        <span v-if="isMobile" class="relative inline-flex" @click.stop="mobileMenuActive = true">
          <feather-icon icon="MoreVerticalIcon" class="cursor-pointer p-2 mr-1" />
          <span v-if="weighingHourCount > 0" class="weighing-bubble more-bubble">{{ weighingHourCount }}</span>
        </span>

        <profile-drop-down />

      </vs-navbar>
    </div>

    <vs-popup
      title="Solar Production"
      :active.sync="solarPopupActive"
      class="solar-popup-wide"
    >
      <SolarProductionModule v-if="solarPopupActive" />
    </vs-popup>

    <vs-popup
      title="Menu"
      :active.sync="mobileMenuActive"
      class="mobile-menu-popup"
    >
      <div class="mobile-menu">
        <div v-if="accessGranted" class="mobile-menu-stats">
          <div class="mobile-stat-card">
            <feather-icon icon="CpuIcon" class="w-4 h-4 text-green-600" />
            <span class="mobile-stat-value">{{ (serverData.cpu || 0).toFixed(0) }}%</span>
            <span class="mobile-stat-label">CPU</span>
          </div>
          <div class="mobile-stat-card">
            <feather-icon icon="BarChartIcon" class="w-4 h-4 text-blue-600" />
            <span class="mobile-stat-value">{{ memoryPercent }}%</span>
            <span class="mobile-stat-label">RAM</span>
          </div>
          <div class="mobile-stat-card">
            <server-area :activeConnectionsList="activeConnectionsList" />
            <span class="mobile-stat-label">Server</span>
          </div>
        </div>

        <router-link
          to="/scale/pesees"
          class="mobile-menu-row"
          @click.native="mobileMenuActive = false"
        >
          <span class="mobile-menu-icon">
            <svg class="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3v18" />
              <path d="M5 21h14" />
              <path d="M5 7h14" />
              <path d="M2 13l3-6 3 6a3 3 0 0 1-6 0z" />
              <path d="M16 13l3-6 3 6a3 3 0 0 1-6 0z" />
            </svg>
          </span>
          <span class="mobile-menu-label">Weighings</span>
          <span v-if="weighingHourCount > 0" class="mobile-menu-badge">{{ weighingHourCount }}</span>
          <feather-icon icon="ChevronRightIcon" class="mobile-menu-chevron" />
        </router-link>

        <a
          v-if="solarStations.length > 0"
          href="#"
          class="mobile-menu-row"
          @click.prevent="openSolarFromMobile"
        >
          <span class="mobile-menu-icon">
            <feather-icon icon="ZapIcon" class="w-5 h-5 text-warning" />
          </span>
          <span class="mobile-menu-label">
            Solar production
            <span
              v-if="solarWorstScore !== null"
              class="solar-score-pill"
              :class="scoreClass"
              style="margin-left: 6px;"
            >
              <feather-icon icon="ActivityIcon" svgClasses="h-3 w-3" class="solar-score-ico" />
              {{ solarWorstScore }}
            </span>
            <span
              v-if="solarCriticalAlertsCount > 0"
              class="solar-alerts-badge pulse"
              style="position: static; margin-left: 6px;"
            >
              <feather-icon icon="BellIcon" svgClasses="h-2-5 w-2-5" class="solar-alerts-bell" />
              {{ solarCriticalAlertsCount }}
            </span>
          </span>
          <span class="mobile-menu-value">
            <span v-if="solarmanOffline" class="solarman-offline-pill">Solarman offline</span>
            <template v-else>{{ solarLiveText }}</template>
          </span>
          <feather-icon icon="ChevronRightIcon" class="mobile-menu-chevron" />
        </a>

        <div v-if="accessGranted" class="mobile-menu-row mobile-menu-action">
          <span class="mobile-menu-icon">
            <feather-icon icon="KeyIcon" class="w-5 h-5 text-primary" />
          </span>
          <span class="mobile-menu-label">Restricted area</span>
          <restricted-area class="mobile-menu-trigger" />
        </div>

        <div class="mobile-menu-row mobile-menu-action">
          <span class="mobile-menu-icon">
            <feather-icon icon="VideoIcon" class="w-5 h-5 text-primary" />
          </span>
          <span class="mobile-menu-label">Calls</span>
          <call-center class="mobile-menu-trigger" />
        </div>

        <div class="mobile-menu-row mobile-menu-action">
          <span class="mobile-menu-icon">
            <feather-icon icon="HelpCircleIcon" class="w-5 h-5 text-primary" />
          </span>
          <span class="mobile-menu-label">Help &amp; Feedback</span>
          <help-button class="mobile-menu-trigger" />
        </div>
      </div>
    </vs-popup>
  </div>
</template>


<script>

import router from '@/router'
import Bookmarks from './components/Bookmarks.vue'
import I18n from './components/I18n.vue'
import SearchBar from './components/SearchBar.vue'
import RestrictedArea from './components/RestrictedArea.vue'
import HelpButton from './components/HelpButton.vue'
import ServerArea from './components/ServerArea.vue'
import CallCenter from './components/CallCenter.vue'
import NotificationDropDown from './components/NotificationDropDown.vue'
import ProfileDropDown from './components/ProfileDropDown.vue'
import SolarProductionModule from "@/views/components/SolarProductionModule.vue";
import moduleBankList from "@/store/bank/moduleDataList.js";
import moduleDataListDashboard from "@/store/dashboard/moduleOperationList.js";
import axios from "@/axios.js";
if (localStorage.getItem('accessToken')) {
  // WELCOME
}
else {
  router.push(router.currentRoute.query.to || '/pages/login')
}

export default {
  data() {
    return {
      accessGranted: false,
      serverData: {
        cpu: 0,
        freeMem: 0,
        uptime: 0,
      },
      activeConnections: 0,
      activeConnectionsList: [],
      weighingHourCount: 0,
      weighingTimer: null,
      solarTimer: null,
      solarPopupActive: false,
      solarTotalPowerW: null,
      mobileMenuActive: false,
    }
  },
  name: 'the-navbar-vertical',
  props: {
    navbarColor: {
      type: String,
      default: '#fff'
    }
  },
  components: {
    Bookmarks,
    I18n,
    SearchBar,
    NotificationDropDown,
    RestrictedArea,
    HelpButton,
    ServerArea,
    CallCenter,
    ProfileDropDown,
    SolarProductionModule
  },
  computed: {
    currentRoute() {
      return this.$route.path;
    },
    navbarColorLocal() {
      if (localStorage.getItem('login-date')) {
        var d1 = new Date();
        var d2 = new Date(localStorage.getItem('login-date'));
        var d3 = ((d1 - d2) / 1000) / 60;
        // CHECK IF TOKEN IS > 23h
        if (d3 > 1380) {
          //GO TO LOCK SCREEN
          // Effacement SÉLECTIF : on préserve 'xylo:trusted-device-token'
          // (comme src/axios.js sur un 401). Un localStorage.clear() effacerait
          // ce token et forcerait un nouveau challenge 2FA à chaque verrouillage
          // 23h — alors que l'appareil est censé rester de confiance 24h.
          const trustedDevice = localStorage.getItem('xylo:trusted-device-token')
          localStorage.clear();
          if (trustedDevice) localStorage.setItem('xylo:trusted-device-token', trustedDevice)
          // Directement à la connexion, en gardant la page demandée : le lien du
          // rapport Harvesting s'ouvre le matin, sur une session de la veille.
          // (L'écran de verrouillage renvoyait à la connexion en perdant le lien.)
          const back = router.currentRoute.query.to || router.currentRoute.fullPath
          router.push({ path: '/pages/login', query: back && back !== '/' ? { to: back } : {} })
        }
      }
      return this.$store.state.theme === 'dark' && this.navbarColor === '#fff' ? '#10163a' : this.navbarColor
    },
    memoryPercent() {
      const { totalMem, freeMem } = this.serverData;
      if (!totalMem || totalMem === 0) return 0;
      return ((1 - (freeMem || 0) / totalMem) * 100).toFixed(0);
    },
    solarStations() {
      return (this.$store.state.dataListDashboard && this.$store.state.dataListDashboard.solarStations) || [];
    },
    solarRealtime() {
      return (this.$store.state.dataListDashboard && this.$store.state.dataListDashboard.solarRealtime) || {};
    },
    solarStationId() {
      return this.solarStations.length > 0 ? this.solarStations[0].id : null;
    },
    solarmanOffline() {
      // Set by the /solar/stations DB-fallback when the Solarman cloud API
      // is unreachable (quota "insufficient allowance", etc.).
      return (
        this.solarStations.length > 0 &&
        this.solarStations[0]._source === "db-fallback"
      );
    },
    solarLiveText() {
      const w = this.solarTotalPowerW;
      if (w === null || w === undefined || isNaN(w)) return "– kW";
      const num = Number(w);
      if (Math.abs(num) >= 1000) return (num / 1000).toFixed(2) + " kW";
      return Math.round(num) + " W";
    },
    solarWorstScore() {
      const w = this.$store.state.dataListDashboard && this.$store.state.dataListDashboard.solarWorstScore;
      return w && typeof w.score === "number" ? w.score : null;
    },
    solarCriticalAlertsCount() {
      const a = this.$store.state.dataListDashboard && this.$store.state.dataListDashboard.solarCriticalAlerts;
      return (a && a.criticalCount) || 0;
    },
    solarWarningAlertsCount() {
      const a = this.$store.state.dataListDashboard && this.$store.state.dataListDashboard.solarCriticalAlerts;
      return (a && a.warningCount) || 0;
    },
    solarTotalAlertsCount() {
      return this.solarCriticalAlertsCount + this.solarWarningAlertsCount;
    },
    bellClass() {
      if (this.solarCriticalAlertsCount > 0) return "bell-red pulse";
      if (this.solarWarningAlertsCount > 0) return "bell-orange";
      return "bell-green";
    },
    scoreClass() {
      return this.scoreClassFor(this.solarWorstScore);
    },
    solarScoresList() {
      return (this.$store.state.dataListDashboard && this.$store.state.dataListDashboard.solarScores) || [];
    },
    solarAlertsList() {
      const a = this.$store.state.dataListDashboard && this.$store.state.dataListDashboard.solarCriticalAlerts;
      return (a && a.items) || [];
    },
    verticalNavMenuWidth() {
      return this.$store.state.verticalNavMenuWidth
    },
    textColor() {
      return { 'text-white': (this.navbarColor !== '#10163a' && this.$store.state.theme === 'dark') || (this.navbarColor !== '#fff' && this.$store.state.theme !== 'dark') }
    },
    windowWidth() {
      return this.$store.state.windowWidth
    },
    isMobile() {
      return this.windowWidth < 768
    },
    // NAVBAR STYLE
    classObj() {
      if (this.verticalNavMenuWidth === 'default') return 'navbar-default'
      else if (this.verticalNavMenuWidth === 'reduced') return 'navbar-reduced'
      else if (this.verticalNavMenuWidth) return 'navbar-full'
    }
  },
  watch: {
    $route(to, from) {
      console.log("Route changed from", from.fullPath, "to", to.fullPath);
      this.onPageChange(to, from);
    }
  },
  mounted() {
    this.fetchServerStatus();
    setInterval(this.fetchServerStatus, 10000); // actualisation chaque 10s

    this.fetchWeighingHourCount();
    this.weighingTimer = setInterval(this.fetchWeighingHourCount, 30000);

    this.initSolarLive();
  },
  beforeDestroy() {
    if (this.weighingTimer) clearInterval(this.weighingTimer);
    if (this.solarTimer) clearInterval(this.solarTimer);
  },
  methods: {
    /* async fetchServerStatus() {
       try {
         //const res = await axios.get("https://xylo.codesinn.com/server_log.json");
         const res = await axios.get("http://localhost:8080/server_log.json");
         this.serverData = res.data;
       } catch (e) {
         alert(e)
         console.error("Erreur serveur:", e);
       }
     },*/

    async fetchServerStatus() {
      if (process.env.NODE_ENV !== 'development') {
      try {
        const res = await axios.get("https://xylo.codesinn.com/server_log.json");
        this.serverData = res.data;

        // Compter le nombre de connexions sur le port 443
        // Filtrer les connexions actives en excluant "LISTEN", "UNCONN", et les adresses locales
        const activeConnections = res.data.connections.filter(connection => {
          return (!connection.state.includes("LISTEN") && !connection.state.includes("UNCONN") && !connection.local.includes("127.0.0.1"));
        });

        // Extraire les IPs distantes uniques
        const activeIps = new Set();
        activeConnections.forEach(connection => {
          // Ajoute l'IP distante dans le Set (les Sets ne contiennent que des valeurs uniques)
          const ip = connection.remote.split(":")[0]; // On récupère l'IP sans le port
          activeIps.add(ip);
        });

        // Compter le nombre d'IP distinctes
        this.activeConnections = activeIps.size;  // Active connections devient le nombre d'IPs distinctes

        // Récupérer les connexions actives et trier par IP distante
        this.activeConnectionsList = res.data.connections.filter(connection => {
          return (!connection.state.includes("LISTEN") && !connection.state.includes("UNCONN") && !connection.local.includes("127.0.0.1"));
        }).sort((a, b) => {
          // Trier par IP distante (remote) par ordre croissant
          if (a.remote < b.remote) return -1;
          if (a.remote > b.remote) return 1;
          return 0;
        });

        console.log('Active connections on port 443:', this.activeConnections);
        console.log('Sorted active connections:', this.activeConnectionsList);

      } catch (e) {
        console.error("Erreur serveur:", e);
      }
    }
    },
    onPageChange(to, from) {
      if (localStorage.getItem("login-roles").toLowerCase().includes("broker")) {
        const url = to.fullPath;
        // Brokers are confined to /broker/* but must keep access to their own
        // profile/security page so they can enable 2FA, change password, etc.
        const brokerAllowedOutsideBrokerSection = [
          "/configurations/edit-profil",
          "/pages/login",
          "/pages/not-authorized"
        ];
        const isAllowed = url.includes("/broker/")
          || brokerAllowedOutsideBrokerSection.some(p => url.startsWith(p));
        if (!isAllowed) {
          this.$vs.notify({
            title: "Denied",
            text: "Redirection",
            iconPack: "feather",
            icon: "icon-alert-circle",
            color: "danger",
            position: "bottom-right",
            time: 8000,
          });
          this.$router.push({ path: "/broker/dashboard" })
        }
      }
    },
    showSidebar() {
      this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true)
    },
    async fetchWeighingHourCount() {
      try {
        const res = await axios.get("/scale/count-hour");
        this.weighingHourCount = Number(res.data && res.data.count) || 0;
      } catch (e) {
        // silent fail — bubble just won't show
      }
    },
    async initSolarLive() {
      try {
        await this.$store.dispatch("dataListDashboard/fetchSolarStations");
        if (this.solarStations.length > 0) {
          await this.refreshSolarLive();
          this.solarTimer = setInterval(this.refreshSolarLive, 30 * 1000);
        }
      } catch (e) {
        // silent fail — solar indicator will remain hidden
      }
    },
    async refreshSolarLive() {
      // Parallel: live realtime W (existing) + DB-backed score + critical alerts (V1).
      await Promise.all([
        this.refreshSolarRealtime(),
        this.$store.dispatch("dataListDashboard/fetchSolarScores").catch(() => {}),
        this.$store.dispatch("dataListDashboard/fetchSolarCriticalAlerts").catch(() => {}),
      ]);
    },
    async refreshSolarRealtime() {
      const ids = this.solarStations.map((s) => s.id);
      if (ids.length === 0) return;
      try {
        const results = await Promise.all(
          ids.map((id) => axios.get(`/solar/realtime/${id}`).then((r) => r.data).catch(() => null))
        );
        const total = results.reduce((acc, d) => {
          const v = d && Number(d.generationPower);
          return acc + (isNaN(v) ? 0 : v);
        }, 0);
        this.solarTotalPowerW = total;
      } catch (e) {
        // keep last known value
      }
    },
    openSolarPopup() {
      this.solarPopupActive = true;
    },
    openSolarFromMobile() {
      this.mobileMenuActive = false;
      this.$nextTick(() => { this.solarPopupActive = true; });
    },
    scoreClassFor(s) {
      if (s === null || s === undefined) return "";
      if (s >= 85) return "score-green";
      if (s >= 60) return "score-orange";
      return "score-red";
    },
    ruleLabel(code) {
      if (code === "R1A_ZERO_PROD") return "No production with sun";
      if (code === "R1B_OFFLINE") return "Inverter offline";
      if (code === "R1C_UNDERPROD") return "Under-production";
      if (code && code.startsWith("NATIVE_")) return "Solarman: " + code.replace("NATIVE_", "");
      return code || "–";
    },
    formatRelativeShort(when) {
      if (!when) return "";
      const ms = Date.parse(when);
      if (!ms || isNaN(ms)) return String(when);
      const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
      if (diffSec < 60) return "now";
      const min = Math.floor(diffSec / 60);
      if (min < 60) return `${min}m`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr}h`;
      return `${Math.floor(hr / 24)}d`;
    },
  },

  created() {
    if (!moduleBankList.isRegistered) {
      this.$store.registerModule("BankList", moduleBankList);
      moduleBankList.isRegistered = true;
    }
    if (!moduleDataListDashboard.isRegistered) {
      this.$store.registerModule("dataListDashboard", moduleDataListDashboard);
      moduleDataListDashboard.isRegistered = true;
    }
    if (localStorage.getItem("login-roles").toLowerCase().includes("admin")) this.accessGranted = true
    let val = { fullPath: window.location.href };
    this.onPageChange(val, "");
  },
}
</script>

<style scoped>
.weighing-icon { color: #6b7280; }
.navbar-icon { color: #6b7280; }
.navbar-icon * { stroke: #6b7280; }
.weighing-bubble {
  position: absolute;
  top: -6px;
  left: 14px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #1E9FF2;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
}

.navbar-group {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  padding: 0 6px;
}
.navbar-group-stats {
  gap: 14px;
}
.navbar-divider {
  display: inline-block;
  width: 1px;
  height: 22px;
  margin: 0 10px;
  background: #e5e7eb;
  vertical-align: middle;
}
.solar-trigger-group {
  gap: 8px;
}
.solar-live-value {
  font-weight: 400;
  color: #6b7280;
  font-size: 14px;
}
.solarman-offline-pill {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: #EA5455;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 22px;
  white-space: nowrap;
}

.solar-score-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 36px;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
  color: #fff;
}
.solar-score-pill .solar-score-ico { opacity: 0.85; }
.solar-score-pill.score-green  { background: #28C76F; }
.solar-score-pill.score-orange { background: #FF9F43; }
.solar-score-pill.score-red    { background: #EA5455; }

.solar-alerts-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 28px;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
}
.solar-alerts-badge.bell-green  { background: #28C76F; }
.solar-alerts-badge.bell-orange { background: #FF9F43; }
.solar-alerts-badge.bell-red    { background: #EA5455; }
.solar-alerts-bell { color: #fff; opacity: 0.9; }
.solar-alerts-badge.pulse {
  animation: pulse-alert 2s ease-in-out infinite;
}
@keyframes pulse-alert {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(234, 84, 85, 0.55);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(234, 84, 85, 0.05);
  }
}

.solar-popover {
  min-width: 320px;
  max-width: 420px;
}
.solar-popover-inner {
  padding: 12px 14px;
  font-size: 13px;
}
.solar-popover-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
}
.solar-popover-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 6px;
  align-items: center;
  padding: 6px 0;
  border-top: 1px solid #f1f3f5;
}
.solar-popover-row:first-of-type { border-top: 0; }
.solar-popover-station { font-weight: 600; }
.solar-popover-score {
  display: inline-block;
  min-width: 28px;
  padding: 2px 6px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 11px;
  text-align: center;
  color: #fff;
}
.solar-popover-score.score-green  { background: #28C76F; }
.solar-popover-score.score-orange { background: #FF9F43; }
.solar-popover-score.score-red    { background: #EA5455; }
.solar-popover-level { color: #6b7280; text-transform: capitalize; }
.solar-popover-breakdown {
  grid-column: 1 / -1;
  font-size: 11px;
  color: #6b7280;
}
.solar-popover-foot {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #eef0f3;
  color: #6b7280;
}
.solar-popover-alert {
  padding: 8px 0;
  border-top: 1px solid #f1f3f5;
}
.solar-popover-alert:first-of-type { border-top: 0; }
.solar-popover-alert .alert-rule {
  margin-left: 8px;
  font-weight: 600;
}
.solar-popover-alert .alert-meta {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}
.alert-sev-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
}
.alert-sev-pill.sev-critical { background: #EA5455; }
.alert-sev-pill.sev-warning  { background: #FF9F43; }
.alert-sev-pill.sev-info     { background: #1E9FF2; }
.weighing-bubble.more-bubble {
  top: -2px;
  left: auto;
  right: -2px;
}
.mobile-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 4px 8px;
}
.mobile-menu-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 4px 4px 14px;
  margin-bottom: 6px;
  border-bottom: 1px solid #eef0f3;
}
.mobile-stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 6px;
  background: #f8fafc;
  border-radius: 10px;
}
.mobile-stat-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
}
.mobile-stat-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  font-weight: 600;
}
.mobile-menu-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 10px 14px;
  border-radius: 12px;
  color: #1f2937;
  font-size: 0.95rem;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease;
}
.mobile-menu-row + .mobile-menu-row {
  border-top: 1px solid #f1f3f6;
  border-radius: 0;
}
.mobile-menu-row:hover,
.mobile-menu-row:active {
  background: #f3f6fa;
}
.mobile-menu-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f1f5f9;
  flex-shrink: 0;
}
.mobile-menu-label {
  flex: 1;
  font-weight: 500;
}
.mobile-menu-chevron {
  width: 18px;
  height: 18px;
  color: #cbd5e1;
  flex-shrink: 0;
}
.mobile-menu-badge {
  min-width: 22px;
  padding: 2px 8px;
  border-radius: 11px;
  background: #ea5455;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}
.mobile-menu-value {
  color: #1f2937;
  font-weight: 600;
  font-size: 0.9rem;
}
.mobile-menu-action .mobile-menu-trigger {
  margin-left: auto;
}
</style>

<style>
.solar-popup-wide .vs-popup {
  width: 90vw !important;
  max-width: 1400px !important;
}
.mobile-menu-popup .vs-popup {
  width: 92vw !important;
  max-width: 420px !important;
  border-radius: 16px !important;
}
.mobile-menu-popup .vs-popup--content {
  padding: 8px 10px 14px !important;
}
.mobile-menu-popup .vs-popup--header {
  border-radius: 16px 16px 0 0 !important;
}
.mobile-menu-popup .mobile-menu-trigger .vs-dropdown--custom,
.mobile-menu-popup .mobile-menu-trigger .help-button-wrapper {
  display: inline-flex;
  align-items: center;
}
.mobile-menu-popup .mobile-menu-trigger .feather {
  margin: 0 !important;
  padding: 4px !important;
}
</style>
